import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import PaginatedTable from '../components/common/PaginatedTable';
import { categoriesApi } from '../api/categories.api';
import { Category } from '../types';
import ConfirmDialog from '../components/common/ConfirmDialog';
import LoadingSpinner from '../components/common/LoadingSpinner';

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Partial<Category>>({ name: '', description: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [deleteId, setDeleteId] = useState(0);

  const loadCategories = async () => {
    try {
      const res = await categoriesApi.getAll();
      setCategories(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCategories(); }, []);

  const handleOpenCreate = () => {
    setSelected({ name: '', description: '' });
    setIsEditing(false);
    setModalOpen(true);
  };

  const handleOpenEdit = (cat: Category) => {
    setSelected({ ...cat });
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      if (isEditing && selected.id) {
        await categoriesApi.update(selected.id, selected);
      } else {
        await categoriesApi.create(selected);
      }
      setModalOpen(false);
      loadCategories();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      await categoriesApi.delete(deleteId);
      setDeleteDialogOpen(false);
      loadCategories();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Categorias</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
          Nueva Categoria
        </Button>
      </Box>

      <PaginatedTable
        columns={[
          { id: 'id', label: 'ID', minWidth: 60 },
          { id: 'name', label: 'Nombre', sx: { fontWeight: 500 } },
          { id: 'description', label: 'Descripcion' },
        ]}
        rows={categories}
        searchFields={['name', 'description']}
        exportFileName="categorias"
        emptyMessage="No hay categorias registradas"
        renderActions={(c) => (
          <>
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(c)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => { setDeleteId(c.id); setDeleteDialogOpen(true); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </>
        )}
      />

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Editar Categoria' : 'Nueva Categoria'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Nombre"
                value={selected.name || ''}
                onChange={(e) => setSelected({ ...selected, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Descripcion"
                value={selected.description || ''}
                onChange={(e) => setSelected({ ...selected, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Eliminar Categoria"
        message="¿Estas seguro de que deseas eliminar esta categoria?"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
};

export default Categories;
